<template>
  <ValidationObserver v-slot="{ handleSubmit, valid }">
    <Loading v-if="loading" />
    <form
      v-else
      class="p-box"
      @submit.prevent="handleSubmit(handleUpdateGmbOpenInfo)"
    >
      <div class="p-box__body">
        <div v-if="gmbOpenInfo && ['closed_temporarily', 'closed_permanently'].includes(gmbOpenInfo.status)">
          <KkInfo
            icon="exclamation"
            class="openStatusCaution"
            alert
          >
            {{ $t('pages.CompaniesLocationStore.openInfoStatusCaution', { openInfoStatus: displayStoreGmbOpenStatus }) }}
          </KkInfo>
        </div>
        <div class="c-info">
          <div class="c-info__icon">
            <Icon name="iconQuestion" />
          </div>
          <div class="c-info__content">
            <p>{{ $t("pages.CompaniesLocationStoreOpenInfoEdit.info") }}</p>
          </div>
        </div>
        <div class="container">
          <div class="p-box">
            <div class="p-box__body statusBox">
              <div class="statusBox__title">
                {{ $t("pages.CompaniesLocationStoreOpenInfoEdit.label") }}
              </div>
              <template v-for="(value, name) in openInfoStatuses" :key="value">
                <div class="c-radio__item">
                  <input
                    :id="name"
                    v-model="openInfoStatus"
                    type="radio"
                    name="status_id"
                    class="c-radio__input"
                    :value="name"
                  >
                  <label :for="name" class="c-radio__label">
                    {{ value }}
                  </label>
                </div>
              </template>
            </div>
          </div>
          <ul class="c-checkbox">
            <li class="c-checkbox__item">
              <input
                id="website_url_synced"
                v-model="statusSynced"
                type="checkbox"
                class="c-checkbox__input"
              >
              <label class="c-checkbox__label" for="website_url_synced">
                {{ $t("pages.CompaniesLocationStoreOpenInfoEdit.protect") }}
              </label>
            </li>
          </ul>
        </div>
      </div>
      <div class="p-box__footer">
        <div class="c-button c-button--right">
          <router-link
            :to="{
              name: 'CompaniesLocationStore',
              params: { company_uuid: companyUuid, store_uuid: storeUuid }
            }"
            class="c-button__item c-button__item--outline-cancel"
          >
            {{ $t("labels.cancel") }}
          </router-link>
          <button
            class="c-button__item c-button__item--primary"
            :class="{ 'c-button__item--disabled': !valid }"
            :disabled="!valid"
          >
            {{ $t("labels.save") }}
          </button>
        </div>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { KkInfo } from '../components/ui'
import Icon from '../components/Icon'
import Loading from '../components/Loading.vue'
import { camelCase } from '../modules/string_converter'
import { gmbLocationOpenInfoStatus } from '../modules/gmbLocationOpenInfoStatus'

export default {
  name: 'CompaniesLocationStoreOpenInfoEdit',
  components: {
    Icon,
    Loading,
    KkInfo,
  },
  emits: ['update-title'],
  data() {
    return {
      openInfoStatus: null,
      statusSynced: null
    }
  },
  computed: {
    ...mapState({
      storeGmb: state => state.companiesStoreGmb.data,
      storeGmbLoading: state => state.companiesStoreGmb.loading,
      gmbOpenInfo: state => state.companiesStoreGmbOpenInfo.data,
      gmbOpenInfoLoading: state => state.companiesStoreGmbOpenInfo.loading
    }),
    companyUuid() {
      return this.$route.params.company_uuid
    },
    storeUuid() {
      return this.$route.params.store_uuid
    },
    loading() {
      return this.storeGmbLoading || this.gmbOpenInfoLoading
    },
    openInfoStatuses() {
      return gmbLocationOpenInfoStatus
    },
    displayStoreGmbOpenStatus() {
      return this.$t(`pages.CompaniesLocationStore.openInfoStatus.${camelCase(this.gmbOpenInfo.status)}`)
    },
    openToClose() {
      if (!this.gmbOpenInfo) return true
      return this.gmbOpenInfo.status === 'open' && this.openInfoStatus !== 'open'
    }
  },
  async created() {
    await this.readStoreGmb({
      company_uuid: this.companyUuid,
      store_uuid: this.storeUuid
    })
    this.statusSynced = this.storeGmb.attributes.open_info_synced
    this.$emit(
      'update-title',
      this.$t('pages.CompaniesLocationStoreOpenInfoEdit.title', {
        name: this.storeGmb.attributes.store_name
      })
    )

    await this.readGmbOpenInfo()
    if(this.gmbOpenInfo) this.openInfoStatus = this.gmbOpenInfo.status
  },
  methods: {
    ...mapActions({
      readStoreGmb: 'companiesStoreGmb/readStoreGmb',
      readGmbOpenInfo: 'companiesStoreGmbOpenInfo/readGmbOpenInfo',
      updateGmbOpenInfo: 'companiesStoreGmbOpenInfo/updateGmbOpenInfo'
    }),
    async handleUpdateGmbOpenInfo() {
      if (
        this.openInfoStatus === 'open' ||
        !this.openToClose ||
        window.confirm(this.$t('pages.CompaniesLocationStoreOpenInfoEdit.openInfoStatusConfirm'))
      ) {
        const payload = {
          store_gmb_open_info: {
            status: this.openInfoStatus,
          },
          store_gmb_location: {
            open_info_synced: this.statusSynced
          }
        }

        if (await this.updateGmbOpenInfo(payload)) this.$router.push({ name: 'CompaniesLocationStore', params: { company_uuid: this.companyUuid, store_uuid: this.storeUuid } })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../stylesheets/biz_spa/foundation/var";
@import "../../../stylesheets/biz_spa/foundation/mixin";
.openStatusCaution {
  margin-bottom: 16px;
  font-weight: bold;
}
.statusBox {
  display: flex;
  flex-direction: column;
  gap: 16px 24px;
  &__title {
    font-weight: bold;
    font-size: 20px;
    line-height: 1.5;
  }
  @include mq(sm) {
    flex-direction: row;
    align-items: center;
  }
}
.c-radio__item {
  padding: 0;
  line-height: 1;
}
.c-checkbox:not(:first-child) {
  margin-top: 24px;
}
</style>
